import React, { createContext, useContext, useState, useEffect } from 'react';

// Available currencies with symbols and exchange rates (approximate)
const CURRENCIES = {
  USD: { symbol: '$', name: 'US Dollar', rate: 1 },
  EUR: { symbol: '€', name: 'Euro', rate: 0.92 },
  GBP: { symbol: '£', name: 'British Pound', rate: 0.79 },
  NGN: { symbol: '₦', name: 'Nigerian Naira', rate: 1550 },
  CAD: { symbol: 'C$', name: 'Canadian Dollar', rate: 1.36 },
  AUD: { symbol: 'A$', name: 'Australian Dollar', rate: 1.53 },
  AED: { symbol: 'د.إ', name: 'UAE Dirham', rate: 3.67 },
  INR: { symbol: '₹', name: 'Indian Rupee', rate: 83.12 },
  ZAR: { symbol: 'R', name: 'South African Rand', rate: 18.50 },
  KES: { symbol: 'KSh', name: 'Kenyan Shilling', rate: 153 },
};

// Available languages
const LANGUAGES = {
  en: { name: 'English', flag: '🇺🇸', nativeName: 'English' },
  fr: { name: 'French', flag: '🇫🇷', nativeName: 'Français' },
  es: { name: 'Spanish', flag: '🇪🇸', nativeName: 'Español' },
  de: { name: 'German', flag: '🇩🇪', nativeName: 'Deutsch' },
  pt: { name: 'Portuguese', flag: '🇵🇹', nativeName: 'Português' },
  ar: { name: 'Arabic', flag: '🇸🇦', nativeName: 'العربية' },
  zh: { name: 'Chinese', flag: '🇨🇳', nativeName: '中文' },
  ha: { name: 'Hausa', flag: '🇳🇬', nativeName: 'Hausa' },
  yo: { name: 'Yoruba', flag: '🇳🇬', nativeName: 'Yorùbá' },
  ig: { name: 'Igbo', flag: '🇳🇬', nativeName: 'Igbo' },
};

// Translation strings
const TRANSLATIONS = {
  en: {
    // Navigation
    flights: 'Flights',
    hotels: 'Hotels',
    events: 'Events',
    vehicles: 'Vehicles',
    visa: 'Visa',
    store: 'Store',
    blog: 'Blog',
    aiPlanner: 'AI Planner',
    dashboard: 'Dashboard',
    wallet: 'Wallet',
    profile: 'Profile',
    settings: 'Settings',
    logout: 'Logout',
    login: 'Login',
    signUp: 'Sign Up',
    // Common
    search: 'Search',
    book: 'Book Now',
    viewDetails: 'View Details',
    price: 'Price',
    from: 'From',
    to: 'To',
    date: 'Date',
    guests: 'Guests',
    rooms: 'Rooms',
    adults: 'Adults',
    children: 'Children',
    // Footer
    quickLinks: 'Quick Links',
    support: 'Support',
    stayUpdated: 'Stay Updated',
    subscribe: 'Subscribe',
    allRightsReserved: 'All rights reserved',
    // Chatbot
    chatWithUs: 'Chat with us',
    typeMessage: 'Type your message...',
    // Currency & Language
    currency: 'Currency',
    language: 'Language',
  },
  fr: {
    flights: 'Vols',
    hotels: 'Hôtels',
    events: 'Événements',
    vehicles: 'Véhicules',
    visa: 'Visa',
    store: 'Boutique',
    blog: 'Blog',
    aiPlanner: 'Planificateur IA',
    dashboard: 'Tableau de bord',
    wallet: 'Portefeuille',
    profile: 'Profil',
    settings: 'Paramètres',
    logout: 'Déconnexion',
    login: 'Connexion',
    signUp: "S'inscrire",
    search: 'Rechercher',
    book: 'Réserver',
    viewDetails: 'Voir les détails',
    price: 'Prix',
    from: 'De',
    to: 'À',
    date: 'Date',
    guests: 'Invités',
    rooms: 'Chambres',
    adults: 'Adultes',
    children: 'Enfants',
    quickLinks: 'Liens rapides',
    support: 'Support',
    stayUpdated: 'Restez informé',
    subscribe: "S'abonner",
    allRightsReserved: 'Tous droits réservés',
    chatWithUs: 'Discutez avec nous',
    typeMessage: 'Tapez votre message...',
    currency: 'Devise',
    language: 'Langue',
  },
  es: {
    flights: 'Vuelos',
    hotels: 'Hoteles',
    events: 'Eventos',
    vehicles: 'Vehículos',
    visa: 'Visa',
    store: 'Tienda',
    blog: 'Blog',
    aiPlanner: 'Planificador IA',
    dashboard: 'Panel',
    wallet: 'Cartera',
    profile: 'Perfil',
    settings: 'Configuración',
    logout: 'Cerrar sesión',
    login: 'Iniciar sesión',
    signUp: 'Registrarse',
    search: 'Buscar',
    book: 'Reservar',
    viewDetails: 'Ver detalles',
    price: 'Precio',
    from: 'Desde',
    to: 'Hasta',
    date: 'Fecha',
    guests: 'Huéspedes',
    rooms: 'Habitaciones',
    adults: 'Adultos',
    children: 'Niños',
    quickLinks: 'Enlaces rápidos',
    support: 'Soporte',
    stayUpdated: 'Mantente informado',
    subscribe: 'Suscribirse',
    allRightsReserved: 'Todos los derechos reservados',
    chatWithUs: 'Chatea con nosotros',
    typeMessage: 'Escribe tu mensaje...',
    currency: 'Moneda',
    language: 'Idioma',
  },
  de: {
    flights: 'Flüge',
    hotels: 'Hotels',
    events: 'Veranstaltungen',
    vehicles: 'Fahrzeuge',
    visa: 'Visum',
    store: 'Geschäft',
    blog: 'Blog',
    aiPlanner: 'KI-Planer',
    dashboard: 'Dashboard',
    wallet: 'Geldbörse',
    profile: 'Profil',
    settings: 'Einstellungen',
    logout: 'Abmelden',
    login: 'Anmelden',
    signUp: 'Registrieren',
    search: 'Suchen',
    book: 'Jetzt buchen',
    viewDetails: 'Details anzeigen',
    price: 'Preis',
    from: 'Von',
    to: 'Nach',
    date: 'Datum',
    guests: 'Gäste',
    rooms: 'Zimmer',
    adults: 'Erwachsene',
    children: 'Kinder',
    quickLinks: 'Schnelllinks',
    support: 'Support',
    stayUpdated: 'Bleib informiert',
    subscribe: 'Abonnieren',
    allRightsReserved: 'Alle Rechte vorbehalten',
    chatWithUs: 'Chatte mit uns',
    typeMessage: 'Nachricht eingeben...',
    currency: 'Währung',
    language: 'Sprache',
  },
  pt: {
    flights: 'Voos',
    hotels: 'Hotéis',
    events: 'Eventos',
    vehicles: 'Veículos',
    visa: 'Visto',
    store: 'Loja',
    blog: 'Blog',
    aiPlanner: 'Planejador IA',
    dashboard: 'Painel',
    wallet: 'Carteira',
    profile: 'Perfil',
    settings: 'Configurações',
    logout: 'Sair',
    login: 'Entrar',
    signUp: 'Cadastrar',
    search: 'Buscar',
    book: 'Reservar',
    viewDetails: 'Ver detalhes',
    price: 'Preço',
    from: 'De',
    to: 'Para',
    date: 'Data',
    guests: 'Hóspedes',
    rooms: 'Quartos',
    adults: 'Adultos',
    children: 'Crianças',
    quickLinks: 'Links rápidos',
    support: 'Suporte',
    stayUpdated: 'Fique atualizado',
    subscribe: 'Inscrever-se',
    allRightsReserved: 'Todos os direitos reservados',
    chatWithUs: 'Fale conosco',
    typeMessage: 'Digite sua mensagem...',
    currency: 'Moeda',
    language: 'Idioma',
  },
  ar: {
    flights: 'رحلات',
    hotels: 'فنادق',
    events: 'فعاليات',
    vehicles: 'مركبات',
    visa: 'تأشيرة',
    store: 'متجر',
    blog: 'مدونة',
    aiPlanner: 'مخطط ذكي',
    dashboard: 'لوحة التحكم',
    wallet: 'المحفظة',
    profile: 'الملف الشخصي',
    settings: 'الإعدادات',
    logout: 'تسجيل خروج',
    login: 'تسجيل دخول',
    signUp: 'إنشاء حساب',
    search: 'بحث',
    book: 'احجز الآن',
    viewDetails: 'عرض التفاصيل',
    price: 'السعر',
    from: 'من',
    to: 'إلى',
    date: 'التاريخ',
    guests: 'الضيوف',
    rooms: 'الغرف',
    adults: 'بالغين',
    children: 'أطفال',
    quickLinks: 'روابط سريعة',
    support: 'الدعم',
    stayUpdated: 'ابق على اطلاع',
    subscribe: 'اشترك',
    allRightsReserved: 'جميع الحقوق محفوظة',
    chatWithUs: 'تحدث معنا',
    typeMessage: 'اكتب رسالتك...',
    currency: 'العملة',
    language: 'اللغة',
  },
  zh: {
    flights: '航班',
    hotels: '酒店',
    events: '活动',
    vehicles: '车辆',
    visa: '签证',
    store: '商店',
    blog: '博客',
    aiPlanner: 'AI规划师',
    dashboard: '仪表板',
    wallet: '钱包',
    profile: '个人资料',
    settings: '设置',
    logout: '退出',
    login: '登录',
    signUp: '注册',
    search: '搜索',
    book: '立即预订',
    viewDetails: '查看详情',
    price: '价格',
    from: '从',
    to: '到',
    date: '日期',
    guests: '客人',
    rooms: '房间',
    adults: '成人',
    children: '儿童',
    quickLinks: '快速链接',
    support: '支持',
    stayUpdated: '保持更新',
    subscribe: '订阅',
    allRightsReserved: '保留所有权利',
    chatWithUs: '与我们聊天',
    typeMessage: '输入您的消息...',
    currency: '货币',
    language: '语言',
  },
  ha: {
    flights: 'Jiragen Sama',
    hotels: 'Otal',
    events: 'Bukukuwa',
    vehicles: 'Motoci',
    visa: 'Biza',
    store: 'Shago',
    blog: 'Blog',
    aiPlanner: 'Mai Tsarawa AI',
    dashboard: 'Dashboard',
    wallet: 'Jakar Kuɗi',
    profile: 'Bayani',
    settings: 'Saituna',
    logout: 'Fita',
    login: 'Shiga',
    signUp: 'Yi Rajista',
    search: 'Bincika',
    book: 'Yi Booking',
    viewDetails: 'Duba Cikakkun Bayanai',
    price: 'Farashi',
    from: 'Daga',
    to: 'Zuwa',
    date: 'Kwanan Wata',
    guests: 'Baƙi',
    rooms: 'Ɗakuna',
    adults: 'Manya',
    children: 'Yara',
    quickLinks: 'Hanyoyin Sauri',
    support: 'Taimako',
    stayUpdated: 'Kasance Cikin Sani',
    subscribe: 'Yi Rajista',
    allRightsReserved: 'An Kiyaye Duk Haƙƙoƙi',
    chatWithUs: 'Yi Magana Da Mu',
    typeMessage: 'Rubuta sakonka...',
    currency: 'Kuɗi',
    language: 'Harshe',
  },
  yo: {
    flights: 'Ọkọ Òfurufú',
    hotels: 'Ilé Ìtura',
    events: 'Àwọn Ìṣẹ̀lẹ̀',
    vehicles: 'Ọkọ̀',
    visa: 'Fisa',
    store: 'Ilé Ìtajà',
    blog: 'Blog',
    aiPlanner: 'Olùgbéròó AI',
    dashboard: 'Dashboard',
    wallet: 'Àpò Owó',
    profile: 'Àkọsílẹ̀',
    settings: 'Ètò',
    logout: 'Jáde',
    login: 'Wọlé',
    signUp: 'Forúkọ sílẹ̀',
    search: 'Wá',
    book: 'Ṣe Ìfipamọ́',
    viewDetails: 'Wo Àlàyé',
    price: 'Iye Owó',
    from: 'Láti',
    to: 'Sí',
    date: 'Ọjọ́',
    guests: 'Àlejò',
    rooms: 'Yàrá',
    adults: 'Àgbà',
    children: 'Ọmọdé',
    quickLinks: 'Àwọn Ìsopọ̀ Kíákíá',
    support: 'Ìrànlọ́wọ́',
    stayUpdated: 'Gbé Ìmọ̀ Tuntun',
    subscribe: 'Forúkọ sílẹ̀',
    allRightsReserved: 'Gbogbo Ẹ̀tọ́ Ni A Pamọ́',
    chatWithUs: 'Sọ̀rọ̀ Pẹ̀lú Wa',
    typeMessage: 'Tẹ ìfiransen rẹ...',
    currency: 'Owó',
    language: 'Èdè',
  },
  ig: {
    flights: 'Ụgbọ Elu',
    hotels: 'Ụlọ Nkwari',
    events: 'Mmemme',
    vehicles: 'Ụgbọ Ala',
    visa: 'Visa',
    store: 'Ụlọ Ahịa',
    blog: 'Blog',
    aiPlanner: 'Onye Nhazi AI',
    dashboard: 'Dashboard',
    wallet: 'Obere Akpa',
    profile: 'Profaịlụ',
    settings: 'Ntọala',
    logout: 'Pụọ',
    login: 'Banye',
    signUp: 'Debanye Aha',
    search: 'Chọọ',
    book: 'Debe',
    viewDetails: 'Lee Nkọwa',
    price: 'Ọnụ Ahịa',
    from: 'Site',
    to: 'Gaa',
    date: 'Ụbọchị',
    guests: 'Ndị Ọbịa',
    rooms: 'Ọnụ Ụlọ',
    adults: 'Ndị Okenye',
    children: 'Ụmụaka',
    quickLinks: 'Njikọ Ngwa Ngwa',
    support: 'Nkwado',
    stayUpdated: 'Nọrọ N\'oge',
    subscribe: 'Debanye Aha',
    allRightsReserved: 'Echekwara Ikike Niile',
    chatWithUs: 'Kparịta Ụka Anyị',
    typeMessage: 'Pịnye ozi gị...',
    currency: 'Ego',
    language: 'Asụsụ',
  },
};

const LocaleContext = createContext();

export const useLocale = () => {
  const context = useContext(LocaleContext);
  if (!context) {
    throw new Error('useLocale must be used within a LocaleProvider');
  }
  return context;
};

export const LocaleProvider = ({ children }) => {
  const [currency, setCurrency] = useState(() => {
    return localStorage.getItem('currency') || 'USD';
  });
  
  const [language, setLanguage] = useState(() => {
    return localStorage.getItem('language') || 'en';
  });

  useEffect(() => {
    localStorage.setItem('currency', currency);
  }, [currency]);

  useEffect(() => {
    localStorage.setItem('language', language);
    // Set document direction for RTL languages
    document.documentElement.dir = language === 'ar' ? 'rtl' : 'ltr';
  }, [language]);

  // Format price with currency
  const formatPrice = (amount, fromCurrency = 'USD') => {
    const currencyInfo = CURRENCIES[currency];
    const fromRate = CURRENCIES[fromCurrency]?.rate || 1;
    const toRate = currencyInfo.rate;
    
    // Convert amount
    const convertedAmount = (amount / fromRate) * toRate;
    
    // Format with locale
    const formatted = new Intl.NumberFormat(language === 'ar' ? 'ar-SA' : 'en-US', {
      minimumFractionDigits: currency === 'NGN' || currency === 'KES' ? 0 : 2,
      maximumFractionDigits: currency === 'NGN' || currency === 'KES' ? 0 : 2,
    }).format(convertedAmount);
    
    return `${currencyInfo.symbol}${formatted}`;
  };

  // Get translation
  const t = (key) => {
    return TRANSLATIONS[language]?.[key] || TRANSLATIONS['en'][key] || key;
  };

  const value = {
    // Currency
    currency,
    setCurrency,
    currencies: CURRENCIES,
    formatPrice,
    currencySymbol: CURRENCIES[currency]?.symbol || '$',
    
    // Language
    language,
    setLanguage,
    languages: LANGUAGES,
    t,
    isRTL: language === 'ar',
  };

  return (
    <LocaleContext.Provider value={value}>
      {children}
    </LocaleContext.Provider>
  );
};

export { CURRENCIES, LANGUAGES, TRANSLATIONS };
